/**
 * ScheduledMessagesPage
 * -----------------------------------------------------------------------------
 * Central de Mensagens Agendadas (/tenant/scheduled-messages).
 *
 * Lista todas as mensagens agendadas do tenant (filtradas por permissões da
 * RPC `list_scheduled_chat_messages`), com KPIs, busca textual, filtros por
 * status e período, e ações por linha (enviar agora, editar, cancelar).
 *
 * Segue o padrão obrigatório de listagem do sistema:
 *   <ListPageLayout>
 *     <ListPageHeader />
 *     <ListPageKpis />
 *     <ListPageFiltersBar />
 *     <ListPageContent />
 *   </ListPageLayout>
 *
 * @version 1.1.0
 * RELEASE: 2026-06-02 v1.1.0 - Usa <ScheduledMessagesTable> compartilhada e
 *          passa campos de macro vinculada (linked_macro_*) ao abrir o dialog
 *          de edição.
 * RELEASE: 2026-06-02 v1.0.0 - Criação da página (Fase 6).
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  Clock,
  Search,
  RefreshCw,
  Inbox,
  Ban,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';


import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  ListPageContent,
  ListPageFooter,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
} from '@/components/layout/list-page';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent } from '@/components/ui/card';

import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { usePermissions } from '@/hooks/usePermissions';
import { ScheduleMessageDialog } from '@/components/tenant/chat/ScheduleMessageDialog';
import { ScheduledMessagesTable } from '@/components/tenant/chat/scheduled/ScheduledMessagesTable';

/**
 * DateRangeField — Linha "De/Até" reutilizada nos três filtros de data
 * (Criação / Agendamento / Envio), seguindo o mesmo padrão visual do
 * DateFilterRow usado em TaskList, CrmDeals, etc.
 */
interface DateRangeFieldProps {
  label: string;
  fromDate: Date | undefined;
  toDate: Date | undefined;
  onFromChange: (d: Date | undefined) => void;
  onToChange: (d: Date | undefined) => void;
}

function DateRangeField({
  label,
  fromDate,
  toDate,
  onFromChange,
  onToChange,
}: DateRangeFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs flex items-center gap-1.5 text-muted-foreground">
        <CalendarDays className="h-3 w-3" />
        {label}
      </Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 justify-start text-xs font-normal"
            >
              {fromDate ? format(fromDate, 'dd/MM/yy') : 'De'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={fromDate}
              onSelect={onFromChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
        <span className="self-center text-xs text-muted-foreground">até</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-8 flex-1 justify-start text-xs font-normal"
            >
              {toDate ? format(toDate, 'dd/MM/yy') : 'Até'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={toDate}
              onSelect={onToChange}
              className="p-3 pointer-events-auto"
              locale={ptBR}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}



/** Item retornado por list_scheduled_chat_messages. */
interface ScheduledItem {
  id: string;
  tenant_id: string;
  conversation_id: string;
  inbox_id: string;
  inbox_name: string;
  inbox_channel_type: string | null;
  contact_id: string;
  contact_name: string;
  contact_phone: string;
  remind_at: string;
  status: string;
  attempts: number;
  cancel_on_reply: boolean;
  cancelled_at: string | null;
  cancel_reason: string | null;
  sent_at: string | null;
  error_details: string | null;
  internal_note: string | null;
  message_payload: any;
  created_by: string | null;
  created_by_name: string | null;
  /** v1.2.0 — avatar do criador (alimenta SmartAvatar na coluna "Criado por"). */
  created_by_avatar?: string | null;
  created_at: string;
  /** v1.2.0 — status de leitura da mensagem enviada (read/delivered/sent/failed/...). */
  message_read_status?: string | null;
  total_count: number;
  // v1.1.0 — campos de macro vinculada retornados pela RPC
  linked_macro_rule_id?: string | null;
  linked_macro_execution_mode?: string | null;
  linked_macro_status?: string | null;
  linked_macro_error?: string | null;
}


const DEFAULT_PAGE_SIZE = 50;

export default function ScheduledMessagesPage() {
  const qc = useQueryClient();
  const { canEdit, canDelete, isAdmin } = usePermissions();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  // Paginação padronizada (1-based) + page size configurável
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // v1.2.0 — Filtros de data (Criação / Agendamento / Envio)
  const [createdFrom, setCreatedFrom] = useState<Date | undefined>();
  const [createdTo, setCreatedTo] = useState<Date | undefined>();
  const [remindFrom, setRemindFrom] = useState<Date | undefined>();
  const [remindTo, setRemindTo] = useState<Date | undefined>();
  const [sentFrom, setSentFrom] = useState<Date | undefined>();
  const [sentTo, setSentTo] = useState<Date | undefined>();

  // Dialog states
  const [editing, setEditing] = useState<ScheduledItem | null>(null);
  // v1.3.0 — Item aberto em modo somente-visualização (status diferente de pending)
  const [viewing, setViewing] = useState<ScheduledItem | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<ScheduledItem | null>(null);
  // Conversa aberta no modal de chat (atalho rápido)
  const [openConversationId, setOpenConversationId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // ----- Filtros enviados às RPCs -----
  // IMPORTANTE: a chave de busca textual é `q` (esperada pelas RPCs
  // list_scheduled_chat_messages e get_scheduled_chat_messages_kpis).
  const filters = useMemo(
    () => ({
      q: search.trim() || undefined,
      status: status !== 'all' ? status : undefined,
    }),
    [search, status],
  );

  /** v1.2.0 — Parâmetros de data passados separadamente para a RPC. */
  const dateParams = useMemo(
    () => ({
      p_created_from: createdFrom ? createdFrom.toISOString() : null,
      p_created_to: createdTo ? createdTo.toISOString() : null,
      p_remind_from: remindFrom ? remindFrom.toISOString() : null,
      p_remind_to: remindTo ? remindTo.toISOString() : null,
      p_sent_from: sentFrom ? sentFrom.toISOString() : null,
      p_sent_to: sentTo ? sentTo.toISOString() : null,
    }),
    [createdFrom, createdTo, remindFrom, remindTo, sentFrom, sentTo],
  );

  /** Quantidade de ranges de data ativos (badge no botão "Datas"). */
  const activeDateRanges =
    (createdFrom || createdTo ? 1 : 0) +
    (remindFrom || remindTo ? 1 : 0) +
    (sentFrom || sentTo ? 1 : 0);

  /** Limpa todos os ranges de data e retorna à primeira página. */
  const clearDateFilters = () => {
    setCreatedFrom(undefined);
    setCreatedTo(undefined);
    setRemindFrom(undefined);
    setRemindTo(undefined);
    setSentFrom(undefined);
    setSentTo(undefined);
    setCurrentPage(1);
  };

  // ----- Query principal (listagem paginada) -----
  const { data, isFetching } = useQuery({
    queryKey: ['scheduled-messages', filters, dateParams, currentPage, pageSize],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'list_scheduled_chat_messages',
        {
          p_filters: filters,
          p_limit: pageSize,
          p_offset: (currentPage - 1) * pageSize,
          ...dateParams,
        } as any,
      );
      if (error) throw error;
      return (data ?? []) as ScheduledItem[];
    },
    staleTime: 15_000,
  });

  // ----- Query dedicada de KPIs (independente da paginação) -----
  // Retorna contagens reais sobre TODO o conjunto filtrado.
  const { data: kpiRow } = useQuery({
    queryKey: ['scheduled-messages-kpis', filters, dateParams],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_scheduled_chat_messages_kpis',
        { p_filters: filters, ...dateParams } as any,
      );
      if (error) throw error;
      return (data?.[0] ?? null) as {
        total: number;
        pending: number;
        processing: number;
        sent: number;
        failed: number;
        cancelled: number;
        skipped_replied: number;
      } | null;
    },
    staleTime: 15_000,
  });


  const total = Number(kpiRow?.total ?? 0);
  const kpis = {
    total,
    pending: Number(kpiRow?.pending ?? 0),
    sent: Number(kpiRow?.sent ?? 0),
    // Falhas/Canceladas/Canceladas por resposta agrupadas em um único card
    failedOrCancelled:
      Number(kpiRow?.failed ?? 0) +
      Number(kpiRow?.cancelled ?? 0) +
      Number(kpiRow?.skipped_replied ?? 0),
  };

  // Invalida lista + KPIs (mantém ambas as caches sincronizadas)
  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['scheduled-messages'] });
    qc.invalidateQueries({ queryKey: ['scheduled-messages-kpis'] });
  };


  // ----- Realtime: atualiza automaticamente quando o status muda -----
  // Inscreve-se em UPDATE/INSERT/DELETE de reminder_queue e invalida o cache
  // do TanStack para que a listagem reflita as mudanças de status instantaneamente.
  useEffect(() => {
    const channel = supabase
      .channel('scheduled-messages-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'reminder_queue',
        },
        (payload) => {
          // Filtra apenas eventos de chat_message para evitar invalidações desnecessárias
          const row = (payload.new ?? payload.old) as { entity_type?: string } | null;
          if (row?.entity_type === 'chat_message') {
            qc.invalidateQueries({ queryKey: ['scheduled-messages'] });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);

  // ----- Ações -----
  const handleSendNow = async (item: ScheduledItem) => {
    setActionLoading(item.id);
    try {
      const { error } = await supabase.rpc('send_scheduled_chat_message_now', {
        p_id: item.id,
      });
      if (error) throw error;
      toast.success('Envio disparado');
      refreshAll();
    } catch (err: any) {
      logger.error('[ScheduledMessages] send_now error:', err);
      toast.error(err?.message || 'Erro ao disparar envio');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!confirmCancel) return;
    setActionLoading(confirmCancel.id);
    try {
      const { error } = await supabase.rpc('cancel_scheduled_chat_message', {
        p_id: confirmCancel.id,
        p_reason: 'Cancelado pelo painel',
      });
      if (error) throw error;
      toast.success('Agendamento cancelado');
      setConfirmCancel(null);
      refreshAll();
    } catch (err: any) {
      logger.error('[ScheduledMessages] cancel error:', err);
      toast.error(err?.message || 'Erro ao cancelar');
    } finally {
      setActionLoading(null);
    }
  };

  const canRowEdit = isAdmin || canEdit('scheduled_messages');
  const canRowDelete = isAdmin || canDelete('scheduled_messages');

  return (
    <>
      <ListPageLayout>
        <ListPageHeader
          title="Mensagens Agendadas"
          description="Acompanhe e gerencie todas as mensagens programadas para envio futuro."
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAll}
              disabled={isFetching}
              className="gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              />
              Atualizar
            </Button>
          }
        />

        <ListPageKpis>
          <KpiCard
            color="purple"
            icon={CalendarClock}
            label="Total no filtro"
            value={kpis.total}
          />
          <KpiCard
            color="yellow"
            icon={Clock}
            label="Pendentes"
            value={kpis.pending}
          />
          <KpiCard
            color="green"
            icon={CheckCircle2}
            label="Enviadas"
            value={kpis.sent}
          />

          <KpiCard
            color="red"
            icon={Ban}
            label="Falhas / Canceladas"
            value={kpis.failedOrCancelled}
          />

        </ListPageKpis>

        <ListPageFiltersBar isFetching={isFetching}>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por contato, telefone ou nota..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className={`${LIST_FILTERS_INPUT_CLASS} pl-8 min-w-[260px]`}
            />
          </div>

          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              setCurrentPage(1);
            }}
          >

            <SelectTrigger className={LIST_FILTERS_SELECT_TRIGGER_CLASS}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
              <SelectItem value="processing">Processando</SelectItem>
              <SelectItem value="sent">Enviada</SelectItem>
              <SelectItem value="failed">Falhou</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
              <SelectItem value="skipped_replied">
                Cancelada (resposta)
              </SelectItem>
            </SelectContent>
          </Select>

          {/* v1.2.0 — Popover "Datas": filtros por Criação / Agendamento / Envio */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`${LIST_FILTERS_SELECT_TRIGGER_CLASS} gap-2`}
              >
                <CalendarDays className="h-4 w-4" />
                Datas
                {activeDateRanges > 0 && (
                  <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                    {activeDateRanges}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[340px] p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Filtros de Data</h4>
                {activeDateRanges > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    onClick={clearDateFilters}
                  >
                    Limpar
                  </Button>
                )}
              </div>

              <DateRangeField
                label="Data Criação"
                fromDate={createdFrom}
                toDate={createdTo}
                onFromChange={(d) => { setCreatedFrom(d); setCurrentPage(1); }}
                onToChange={(d) => { setCreatedTo(d); setCurrentPage(1); }}
              />
              <DateRangeField
                label="Data Agendamento"
                fromDate={remindFrom}
                toDate={remindTo}
                onFromChange={(d) => { setRemindFrom(d); setCurrentPage(1); }}
                onToChange={(d) => { setRemindTo(d); setCurrentPage(1); }}
              />
              <DateRangeField
                label="Data Envio"
                fromDate={sentFrom}
                toDate={sentTo}
                onFromChange={(d) => { setSentFrom(d); setCurrentPage(1); }}
                onToChange={(d) => { setSentTo(d); setCurrentPage(1); }}
              />
            </PopoverContent>
          </Popover>
        </ListPageFiltersBar>


        <ListPageContent>
          {(!data || data.length === 0) && !isFetching ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Inbox className="h-10 w-10 text-muted-foreground/40 mb-3" />
                <h3 className="text-base font-semibold">
                  Nenhuma mensagem agendada
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  Agende mensagens diretamente do chat clicando no ícone de
                  relógio no composer.
                </p>
              </CardContent>
            </Card>
          ) : (
            <ScheduledMessagesTable
              items={(data ?? []) as any}
              actionLoadingId={actionLoading}
              canRowEdit={canRowEdit}
              canRowDelete={canRowDelete}
              showInboxColumn
              onOpenConversation={(id) => setOpenConversationId(id)}
              onEdit={(item) => setEditing(item as unknown as ScheduledItem)}
              onSendNow={(item) => handleSendNow(item as unknown as ScheduledItem)}
              onCancel={(item) =>
                setConfirmCancel(item as unknown as ScheduledItem)
              }
              // v1.3.0 — clique na linha abre o modal: edição quando pendente,
              // visualização (readOnly) para qualquer outro status.
              onRowClick={(item) => {
                const it = item as unknown as ScheduledItem;
                if (it.status === 'pending' && canRowEdit) {
                  setEditing(it);
                } else {
                  setViewing(it);
                }
              }}
            />
          )}

        </ListPageContent>

        {/* Rodapé padronizado de paginação server-side */}
        {!isFetching && total > 0 && (
          <ListPageFooter>
            <PaginationFooter
              totalItems={total}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
            />
          </ListPageFooter>
        )}
      </ListPageLayout>


      {/* Dialog de edição (status = pending) */}
      {editing && (
        <ScheduleMessageDialog
          open={!!editing}
          onOpenChange={(v) => !v && setEditing(null)}
          scheduledId={editing.id}
          conversationContext={{
            inboxId: editing.inbox_id,
            inboxName: editing.inbox_name,
            // Usa channel_type real da RPC (ex.: whatsapp_oficial) para
            // habilitar a UI de janela 24h e os pickers de template.
            channelType: editing.inbox_channel_type ?? 'whatsapp',
          }}
          initial={{
            scheduledAt: editing.remind_at,
            text:
              editing.message_payload?.text ||
              editing.message_payload?.caption ||
              '',
            cancelOnReply: editing.cancel_on_reply,
            internalNote: editing.internal_note ?? '',
            linkedMacroRuleId: editing.linked_macro_rule_id ?? null,
            linkedMacroMode:
              (editing.linked_macro_execution_mode as any) ?? null,
            // v1.3.0 — payload bruto para hidratar template/fallback
            messagePayload: editing.message_payload,
          }}
          onSuccess={() => {
            setEditing(null);
            refreshAll();
          }}
        />
      )}

      {/* v1.3.0 — Dialog de visualização (status != pending): mesmo componente
          em modo readOnly para não permitir alteração de mensagens já enviadas
          ou canceladas. */}
      {viewing && (
        <ScheduleMessageDialog
          open={!!viewing}
          onOpenChange={(v) => !v && setViewing(null)}
          scheduledId={viewing.id}
          readOnly
          conversationContext={{
            inboxId: viewing.inbox_id,
            inboxName: viewing.inbox_name,
            channelType: viewing.inbox_channel_type ?? 'whatsapp',
          }}
          initial={{
            scheduledAt: viewing.remind_at,
            text:
              viewing.message_payload?.text ||
              viewing.message_payload?.caption ||
              '',
            cancelOnReply: viewing.cancel_on_reply,
            internalNote: viewing.internal_note ?? '',
            linkedMacroRuleId: viewing.linked_macro_rule_id ?? null,
            linkedMacroMode:
              (viewing.linked_macro_execution_mode as any) ?? null,
            messagePayload: viewing.message_payload,
          }}
        />
      )}

      {/* Confirm cancel */}
      <AlertDialog
        open={!!confirmCancel}
        onOpenChange={(v) => !v && setConfirmCancel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar agendamento?</AlertDialogTitle>
            <AlertDialogDescription>
              A mensagem programada para{' '}
              {confirmCancel &&
                format(
                  new Date(confirmCancel.remind_at),
                  "dd/MM/yyyy 'às' HH:mm",
                  { locale: ptBR },
                )}{' '}
              não será enviada. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, cancelar envio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de chat (atalho rápido para visualizar a conversa) */}
      <ChatConversationModal
        conversationId={openConversationId}
        open={!!openConversationId}
        onOpenChange={(v) => !v && setOpenConversationId(null)}
      />
    </>
  );
}
